import { AxiosError } from 'axios';
import * as React from 'react';
import styled from 'styled-components';

import useFocus from 'src/CustomHooks/useFocus';
import useProgressBar from 'src/CustomHooks/useProgressBar';
import ISubmission from 'src/ServerEntities/Submission';
import Button from 'src/SharedComponents/Button';
import ButtonContainer from 'src/SharedComponents/ButtonContainer';
import ContentWrapper from 'src/SharedComponents/ContentWrapper';
import ErrorToast from 'src/SharedComponents/ErrorToast';
import LoadingIndicator from 'src/SharedComponents/LoadingIndicator';
import ProgressBar from 'src/SharedComponents/ProgressBar';
import Switch from 'src/SharedComponents/Switch';
import DialogScaleMultipleChoiceQuestion from './DialogScaleMultipleChoiceQuestion';

import { getAnswers, saveAnswer, saveHelp, submitForm } from 'src/service';
import { SessionContext } from 'src/Views/SessionContext';

import { PageAnswered, PageCurrent, PageList, PageNotAnswered } from './DialogFunctions';


const QuestionContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    align-content: center;
    width: 100%;
`;

const HelpContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    align-content: center;
    width: 100%;
`;

const choices = ["1", "2", "3", "4", "5", "6", "7"];
const QUESTION_IDS = [94, 95, 96, 97, 98, 99, 100, 101, 102, 103, 104];
const questions = [
    "How satisfied are you with your mental health?",
    "How satisfied are you with your physical health?",
    "How satisfied are you with your job situation?",
    "How satisfied are you with your accomodation?",
    "How satisfied are you with your leisure activities?",
    "How satisfied are you with your relationship with your partner/family?",
    "How satisfied are you with your friendships?",
    "How satisfied are you with your personal safety?",
    "How satisfied are you with your medication?",
    "How satisfied are you with the practical help you receive?",
    "How satisfied are you with your meetings with mental health professionals?"
];

interface IProps {
    token: string
};

const DialogScalePage = (props: IProps) => {
    const [loading, setLoading] = React.useState(false);
    const {htmlElRef, setFocus} = useFocus<HTMLDivElement>();
    const [incomplete, setIncomplete] = React.useState(true);
    const { dispatch, state } = React.useContext(SessionContext);
    const help = state.dialogAnswerHelp;
    const [answered, setAnswered] = React.useState([-1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1]);
    const currentPage = state.page?.page ? state.page?.page - 1 : 0;
    const webToken = state.webToken;
    const [error, setError] = React.useState("");
    const { numberAnswered, updateAllAnswered, updateAnswered, ERROR_SAVING_MESSAGE } = useProgressBar();
    const questionRefs = QUESTION_IDS.map(questionId => React.useRef(null as unknown as HTMLDivElement));

    const checkUnauthorised = (serverError: AxiosError) => {
        if (serverError.response && serverError.response.data.code === 401) {
            dispatch({ type: "clearWebToken", payload: undefined });
        }
    }

    const handleError = (serverError: AxiosError, questionId: number) => {
        checkUnauthorised(serverError);
        setError(ERROR_SAVING_MESSAGE);
        updateAnswered(questionId, false, -1);
    };

    const handleSubmissionError = (serverError: AxiosError) => {
        checkUnauthorised(serverError);
        setError(ERROR_SAVING_MESSAGE);
    };

    const checkEnoughAnswered = () => {
        return !answered.includes(-1);
    };

    const onAnswerChange = (questionId: number, answer: string, itemIndex?: number, autoForward?: boolean) => {
        setError("");
        saveAnswer(questionId, answer, state.webToken, () => {
            const arrCopy = [...answered];
            arrCopy[currentPage] = itemIndex !== undefined ? itemIndex : -1;
            if (currentPage === 10 && !arrCopy.includes(-1) && autoForward) {
                goToNextPageIfComplete();
            } else {
                setAnswered(arrCopy);
            }
        }, handleError)
    };

    const goToNextPageIfComplete = () => {
        setLoading(true);
        submitForm(state.webToken, () => {
            dispatch({ type: "setPage", payload: { formId: 4, page: 12 } });
        }, handleSubmissionError);
    };

    const goToNextPage = () => {
        if (answered[currentPage] === -1) {
            onAnswerChange(QUESTION_IDS[currentPage], choices[3], 3, true);
        } else if (currentPage === 10) {
            goToNextPageIfComplete();
        }
        if (currentPage + 1 <= answered.length) {
            dispatch({ type: "setPage", payload: { formId: 4, page: currentPage + 2 } });
        }
    };

    const goToPreviousPage = () => {
        if (currentPage > 0) {
            dispatch({ type: "setPage", payload: { formId: 4, page: currentPage } });
        }
    };

    const moveToPage = (index: number) => (e: any) => {
        dispatch({ type: "setPage", payload: { formId: 4, page: index } });
    };

    const keyMoveToPage = (index: number) => (e: React.KeyboardEvent) => {
        if (e.key === " ") {
            e.stopPropagation();
            e.preventDefault();
            dispatch({ type: "setPage", payload: { formId: 4, page: index } });
        }
    };

    const pageToItem = (current: number) => (answer: number, index: number) => {
        const pageNumber = index + 1;
        if (index === current) {
            return <PageCurrent key={`page-number-${index}`} onKeyDown={keyMoveToPage(pageNumber)} href="javascript:;" onClick={moveToPage(pageNumber)}>{pageNumber}</PageCurrent>;
        }
        if (answer > -1) {
            return <PageAnswered key={`page-number-${index}`} onKeyDown={keyMoveToPage(pageNumber)} href="javascript:;" onClick={moveToPage(pageNumber)}>{pageNumber}</PageAnswered>;
        }
        return <PageNotAnswered key={`page-number-${index}`} onKeyDown={keyMoveToPage(pageNumber)} href="javascript:;" onClick={moveToPage(pageNumber)}>{pageNumber}</PageNotAnswered>;
    };

    const onSubmit = () => {
        if (!checkEnoughAnswered()) {
            setIncomplete(true);
            setFocus();
        } else {
            goToNextPageIfComplete();
        }
    };

    if (checkEnoughAnswered() && incomplete) {
        setIncomplete(false);
    }

    const onHelpSwitch = (questionId: number) => () => {
        help[currentPage] = !help[currentPage];
        saveHelp(questionId, choices[answered[currentPage]], help[currentPage], state.webToken, () => {
            dispatch({ type: "setDialogAnswerHelp", payload: help });
        }, handleError)
    };

    React.useEffect(() => {
        if (webToken) {
            getAnswers(props.token, webToken, (submissionResponse: ISubmission) => {
                const arrCopy = [...answered];
                const questionAnswers = submissionResponse.answers.map(a => {
                    return {
                        isAnswered: true,
                        itemIndex: choices.indexOf(a.answer),
                        questionId: a.questionId
                    };
                });
                submissionResponse.answers.forEach(a => {
                    const questionIndex = questions.indexOf(a.question);
                    arrCopy[questionIndex] = choices.indexOf(a.answer);
                    help[questionIndex] = a.help
                });
                updateAllAnswered(questionAnswers);
                setAnswered(arrCopy);
                dispatch({ type: "setDialogAnswerHelp", payload: help });
            }, (aError: AxiosError) => {
                setError("There was an error loading previous answers. Please fill in all questions.");
            });
        }
    }, [webToken]);

    React.useEffect(() => {
        setFocus();
    }, [currentPage]);

    return <ContentWrapper>
        <PageList>
            {answered.map(pageToItem(currentPage))}
        </PageList >
        <QuestionContainer>
            <DialogScaleMultipleChoiceQuestion
                inputRef={htmlElRef}
                checkedItem={answered[currentPage]}
                refProp={questionRefs[currentPage]}
                question={questions[currentPage]}
                questionId={QUESTION_IDS[currentPage]}
                onAnswerChange={onAnswerChange}
                choices={choices} />
        </QuestionContainer>
        <HelpContainer>
            <label htmlFor="more-help-switch">Do you need more help in this area?</label>
            <div>No<Switch checked={help[currentPage]} onChange={onHelpSwitch(QUESTION_IDS[currentPage])} />Yes</div>
        </HelpContainer>
        <ButtonContainer style={{ justifyContent: "center", marginTop: "50px", marginBottom: "50px", flexWrap: "wrap" }}>
            {loading && <LoadingIndicator />}
            {!loading && <Button text="Previous" onClick={goToPreviousPage} />}
            {!loading && <Button text="Next" onClick={goToNextPage} />}
            {!loading && <Button text="Submit answers" onClick={onSubmit} disabled={incomplete} />}
        </ButtonContainer>
        <ProgressBar length={11} progress={numberAnswered} />
        <ErrorToast className={error ? "sliding" : "hidden"}>{error}</ErrorToast>
    </ContentWrapper>;
};

export default DialogScalePage;




// WEBPACK FOOTER //
// ./src/Forms/dialog/DialogScalePage1.tsx